import { FC, useMemo, useRef, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { CRON_EXAMPLES } from "../../constants/hints"
import { Userbot } from "../../interfaces"
import {
  useCreateScheduledChainMutation,
  useUpdateScheduledChainMutation,
} from "../../store"
import { ChainBuilder } from "../chains/chainBuilder"
import {
  ChainFormState,
  Descriptors,
  chainStateFromDto,
} from "../chains/chainSerializer"
import { LaunchTargetFields } from "../common/launchTargetFields"
import {
  LaunchTargetState,
  launchTargetFromDto,
  serializeLaunchTarget,
  validateLaunchTarget,
} from "../common/launchTarget"
import { ConfirmDialog } from "../common/confirmDialog"
import { getApiErrorMessage } from "@/core/utils"
import { useCatalog, useUserbotWriteAccess } from "../../hooks"

const TIMEZONES: string[] = (() => {
  try {
    return ["UTC", "Etc/UTC", ...Intl.supportedValuesOf("timeZone")]
  } catch {
    return [
      "UTC",
      "Europe/Moscow",
      "Europe/London",
      "America/New_York",
      "Asia/Tokyo",
    ]
  }
})()

interface IProps {
  scheduled?: Userbot.ScheduledChain
  onDone?: () => void
}

export const ScheduledChainForm: FC<IProps> = ({ scheduled, onDone }) => {
  const isEdit = !!scheduled
  const canWrite = useUserbotWriteAccess()

  const [name, setName] = useState(scheduled?.name ?? "")
  const [target, setTarget] = useState<LaunchTargetState>(() =>
    launchTargetFromDto(scheduled ?? {}),
  )
  const [cron, setCron] = useState(scheduled?.cron ?? "")
  const [tz, setTz] = useState(scheduled?.tz ?? "")
  const [overlap, setOverlap] = useState<"skip" | "allow">(
    scheduled?.overlap ?? "skip",
  )
  const [isActive, setIsActive] = useState(scheduled?.isActive ?? false)
  const [confirmEnable, setConfirmEnable] = useState(false)
  // Omission preserves the stored body; a subsequent invalid builder edit is null.
  const [template, setTemplate] = useState<
    Userbot.ChainTemplate | null | undefined
  >(undefined)
  const [error, setError] = useState<string | null>(null)
  const inFlight = useRef(false)
  const [busy, setBusy] = useState(false)

  const catalog = useCatalog()
  const descriptorsMap = useMemo<Descriptors>(
    () =>
      catalog.actions.reduce((acc, d) => {
        acc[d.name] = d
        return acc
      }, {} as Descriptors),
    [catalog.actions],
  )

  const initialBuilderState: ChainFormState | undefined = scheduled
    ? chainStateFromDto(scheduled.chainTemplate, descriptorsMap)
    : undefined

  const [createScheduled, createState] = useCreateScheduledChainMutation()
  const [updateScheduled, updateState] = useUpdateScheduledChainMutation()
  const loading = busy || createState.isLoading || updateState.isLoading

  const handleSubmit = async () => {
    if (!canWrite || inFlight.current) return
    setError(null)
    const errs: string[] = []
    if (!name.trim()) errs.push("Укажите название")
    if (!cron.trim()) errs.push("Укажите cron")
    const body = template === undefined ? scheduled?.chainTemplate : template
    errs.push(...validateLaunchTarget(target))
    if (!body) errs.push("Заполните шаблон цепочки корректно")
    if (errs.length) {
      setError(errs.join("\n"))
      return
    }

    inFlight.current = true
    setBusy(true)
    try {
      if (isEdit) {
        await updateScheduled({
          id: scheduled!.id,
          body: {
            name,
            cron,
            tz: tz || undefined,
            overlap,
            isActive,
            chainTemplate: body!,
          },
        }).unwrap()
      } else {
        await createScheduled({
          body: {
            name,
            cron,
            tz: tz || undefined,
            overlap,
            ...serializeLaunchTarget(target),
            isActive: false,
            chainTemplate: template!,
          },
        }).unwrap()
      }
      onDone?.()
    } catch (e) {
      setError(getApiErrorMessage(e))
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <TextField
        label="Название расписания"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        fullWidth
      />

      {!isEdit ? (
        <LaunchTargetFields value={target} onChange={setTarget} />
      ) : (
        <Alert severity="info">
          Исполнитель неизменяем: {scheduled.role ?? scheduled.clientId ?? "—"}.{" "}
          {scheduled.role
            ? `${scheduled.roleMode ?? "one"} / ${scheduled.targetUnit ?? "client"}`
            : ""}{" "}
          / {scheduled.targetFilter ?? "без фильтра"}
          {scheduled.targetFilterParams?.maxAgeDays !== undefined
            ? ` / maxAgeDays: ${scheduled.targetFilterParams.maxAgeDays}`
            : ""}
        </Alert>
      )}
      {!isEdit && (
        <Alert severity="info">
          Новое расписание будет создано неактивным. Включение — отдельное
          подтверждённое действие.
        </Alert>
      )}

      <Box>
        <TextField
          label="Cron"
          value={cron}
          onChange={(e) => setCron(e.target.value)}
          required
          fullWidth
          helperText="Формат BullMQ, напр. 0 9 * * *"
        />
        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
          {(catalog.cronPresets.length
            ? catalog.cronPresets.map((p) => ({
                label: p.label,
                value: p.cron,
              }))
            : CRON_EXAMPLES
          ).map((ex) => (
            <Chip
              key={ex.value}
              size="small"
              variant="outlined"
              label={`${ex.label} (${ex.value})`}
              onClick={() => setCron(ex.value)}
            />
          ))}
        </Stack>
      </Box>

      <Autocomplete
        options={TIMEZONES}
        value={tz || null}
        onChange={(_, v) => setTz(v ?? "")}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Часовой пояс"
            helperText="IANA, напр. Europe/Moscow. Пусто = UTC"
          />
        )}
      />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Перекрытие запусков
        </Typography>
        <RadioGroup
          row
          value={overlap}
          onChange={(e) => setOverlap(e.target.value as "skip" | "allow")}
        >
          <FormControlLabel
            value="skip"
            control={<Radio />}
            label="Пропускать"
          />
          <FormControlLabel
            value="allow"
            control={<Radio />}
            label="Разрешать"
          />
        </RadioGroup>
      </Box>

      {isEdit && (
        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={(e) => {
                e.target.checked ? setConfirmEnable(true) : setIsActive(false)
              }}
            />
          }
          label="Активно"
        />
      )}
      <ConfirmDialog
        open={confirmEnable}
        title="Включить расписание?"
        description="Cron начнёт запускать цепочки. Подтвердите разрешение на регулярные запуски."
        onCancel={() => setConfirmEnable(false)}
        onConfirm={() => {
          setIsActive(true)
          setConfirmEnable(false)
        }}
      />

      <Divider textAlign="left">
        <Typography variant="subtitle2">Шаблон цепочки</Typography>
      </Divider>

      <ChainBuilder
        key={isEdit ? `builder-${catalog.actions.length > 0}` : undefined}
        mode="template"
        value={initialBuilderState}
        onChange={(_, tpl) => setTemplate(tpl)}
      />

      <Stack direction="row" justifyContent="flex-end">
        {canWrite && (
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            Сохранить
          </Button>
        )}
      </Stack>
    </Stack>
  )
}
